import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from './AuthContext';
import CustomerMenu from './CustomerMenu';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import {
  Package, Bookmark, ArrowRight, Loader2, Trash2, ShoppingCart
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerDesigns = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, login } = useAuth();
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading || !user) return;
    const fetchDesigns = async () => {
      try {
        const token = localStorage.getItem('kaha_session');
        const res = await axios.get(`${API}/customer/designs`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setDesigns(res.data);
      } catch {
        toast.error('Error al cargar diseños');
      }
      setLoading(false);
    };
    fetchDesigns();
  }, [user, authLoading]);

  const handleDelete = async (designId) => {
    if (!window.confirm('¿Eliminar este diseño guardado?')) return;
    try {
      const token = localStorage.getItem('kaha_session');
      await axios.delete(`${API}/customer/designs/${designId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDesigns((prev) => prev.filter((d) => d.id !== designId));
      toast.success('Diseño eliminado');
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const handleUseDesign = (design) => {
    const params = new URLSearchParams({
      length: design.dimensions?.length || '',
      width: design.dimensions?.width || '',
      height: design.dimensions?.height || '',
      material_id: design.material_id || '',
      quantity: design.quantity || 1,
    });
    navigate(`/configurar?${params.toString()}`);
  };

  if (authLoading) return null;
  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center space-y-4">
            <Bookmark className="w-12 h-12 text-accent mx-auto" />
            <h2 className="text-xl font-bold text-primary">Inicia sesión</h2>
            <p className="text-muted-foreground text-sm">Para ver tus diseños guardados necesitas iniciar sesión.</p>
            <Button onClick={() => login()} className="bg-accent text-white hover:bg-accent/90 rounded-full px-6" data-testid="designs-login-btn">
              Iniciar sesión
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background font-manrope">
      <header className="border-b bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2" data-testid="logo-link">
            <Package className="w-8 h-8 text-accent" />
            <span className="text-xl font-bold text-primary">KAHA</span>
          </a>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/configurar')} className="gap-2 text-muted-foreground hover:text-primary" data-testid="designs-new-box-btn">
              <ArrowRight className="w-4 h-4" />
              <span className="hidden sm:inline">Nueva caja</span>
            </Button>
            <CustomerMenu />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-primary">Diseños guardados</h1>
            <p className="text-sm text-muted-foreground mt-1">{designs.length} diseño{designs.length !== 1 ? 's' : ''}</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-accent" />
          </div>
        ) : designs.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center space-y-4">
              <Bookmark className="w-16 h-16 text-muted-foreground/30 mx-auto" />
              <h3 className="text-lg font-semibold text-primary">Sin diseños guardados</h3>
              <p className="text-sm text-muted-foreground">Configura una caja y guardala como diseño para reutilizar despues.</p>
              <Button onClick={() => navigate('/configurar')} className="bg-accent text-white hover:bg-accent/90 rounded-full px-6" data-testid="designs-create-btn">
                Crear mi caja
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {designs.map((d) => (
              <Card key={d.id} className="hover:border-accent/30 transition-colors group" data-testid={`design-card-${d.id}`}>
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-start justify-between">
                    <h3 className="font-bold text-primary text-base truncate pr-2">{d.name}</h3>
                    <button
                      onClick={() => handleDelete(d.id)}
                      className="text-muted-foreground hover:text-red-500 transition-colors p-1 opacity-0 group-hover:opacity-100"
                      data-testid={`design-delete-${d.id}`}
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="space-y-1 text-sm text-muted-foreground">
                    <p>{d.dimensions?.length}x{d.dimensions?.width}x{d.dimensions?.height} mm</p>
                    <p>{d.material_name || '-'}</p>
                    <p>{d.quantity} caja{d.quantity !== 1 ? 's' : ''}</p>
                    {d.printing?.has_printing && (
                      <p className="text-accent text-xs font-medium">Con impresión</p>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground/60">
                    {new Date(d.created_at).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </p>
                  <Button
                    onClick={() => handleUseDesign(d)}
                    variant="outline"
                    size="sm"
                    className="w-full gap-1.5 mt-2 border-accent/40 text-accent hover:bg-accent/5"
                    data-testid={`design-use-${d.id}`}
                  >
                    <ShoppingCart className="w-3.5 h-3.5" />
                    Usar este diseño
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default CustomerDesigns;
